/**
 * Resume Editor Modal Component
 * Requirements: 4.1, 4.2, 4.4, 1.1, 1.2, 1.3, 1.4, 2.1, 2.2, 2.3, 2.4, 2.5, 3.1, 3.2, 3.3, 3.4, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 7.1, 7.2, 7.3, 7.4
 */
import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { X, Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown, Edit2, Building2, Briefcase, Upload, Download, FileText, FileType, AlertTriangle, Sparkles, Check, XCircle, ExternalLink, Copy, Brain, Settings, SlidersHorizontal, HelpCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { UpgradePromptModal } from './UpgradePromptModal';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { importResumeFile, ImportValidationError, ImportUploadError, ImportProcessError, ImportAuthError, ImportTimeoutError } from '../services/importService';
import type { ResumeJSON, Recommendation, ContactItem, Education, Award, Presentation, Publication, Portfolio, ExperienceCompany, ExperienceRole, ExperienceBullet, UserPreferences, SubcomponentType, GenerationType } from '../types';
import { GENERATION_TYPE_RESTRICTIONS } from '../types';
import { GENERATION_TYPE_LABELS } from '../constants';
import yaml from 'js-yaml';
import { splitBulletIntoLines, hasOrphan } from '../utils/pdfLineBreaks';

interface ResumeEditorProps {
  isOpen: boolean;
  onClose: () => void;
  // savedName is the resume name that was just persisted. The parent should
  // refresh lists AND set its editing-resume state to savedName so a brand-new
  // resume transitions from create-mode to edit-mode without closing the modal.
  onSuccess: (savedName?: string) => void;
  resumename?: string;
  onDeleteResume?: (resumename: string) => Promise<void>;
}

// Resume HTML typography constants — keep in sync with skins/default/style.css
// Page: max-width 8.5in, padding 0.6in each side → content width 7.3in = 700.8px
// Experience bullets: indent ~2em at 9.5pt inside content area ≈ 675px
// Footer columns (education/awards/presentations): 50% of 700.8px ≈ 350px
const RESUME_FONT = "'Helvetica Neue', Helvetica, Arial, 'Segoe UI', sans-serif";
const RESUME_WIDTH = 771;          // 7.3in content area in px (+10% for wider lightbox)
const RESUME_BULLET_WIDTH = 743;   // content area minus exp-role + bullet indent (+10%)

// Default icon options — used as fallback until global settings are fetched
const DEFAULT_ICON_OPTIONS: { value: ContactItem['icon']; label: string; svg: string; placeholder: string }[] = [
  { value: 'email-at', label: 'Email', svg: '/icons/email-at.svg', placeholder: 'mailto:email@example.com' },
  { value: 'phone', label: 'Phone', svg: '/icons/phone.svg', placeholder: 'tel:+18889871234' },
  { value: 'phone-volume', label: 'Phone Alt', svg: '/icons/phone-volume.svg', placeholder: 'tel:+18889871234' },
  { value: 'linkedin', label: 'LinkedIn', svg: '/icons/linkedin.svg', placeholder: 'https://www.linkedin.com/in/username' },
  { value: 'x-twitter', label: 'X/Twitter', svg: '/icons/x-twitter.svg', placeholder: 'https://x.com/username' },
  { value: 'bluesky', label: 'Bluesky', svg: '/icons/bluesky.svg', placeholder: 'https://bsky.app/profile/username.bsky.social' },
  { value: 'github', label: 'GitHub', svg: '/icons/github.svg', placeholder: 'https://github.com/username' },
  { value: 'github-square', label: 'GitHub Square', svg: '/icons/github-square.svg', placeholder: 'https://github.com/username' },
  { value: 'globe', label: 'Website', svg: '/icons/globe.svg', placeholder: 'https://your.website.com' },
  { value: 'house', label: 'Home', svg: '/icons/house.svg', placeholder: 'https://www.google.com/maps/place/San+Francisco+Bay+Area' },
  { value: 'facebook', label: 'Facebook', svg: '/icons/facebook.svg', placeholder: 'https://www.facebook.com/username' },
  { value: 'instagram', label: 'Instagram', svg: '/icons/instagram.svg', placeholder: 'https://www.instagram.com/username' },
  { value: 'tiktok', label: 'TikTok', svg: '/icons/tiktok.svg', placeholder: 'https://www.tiktok.com/@username' },
  { value: 'youtube', label: 'YouTube', svg: '/icons/youtube.svg', placeholder: 'https://www.youtube.com/@username' },
  { value: 'discord', label: 'Discord', svg: '/icons/discord.svg', placeholder: 'https://discord.com/users/username' },
  { value: 'slack', label: 'Slack', svg: '/icons/slack.svg', placeholder: 'https://yourworkspace.slack.com/team/yourslackid' },
  { value: 'telegram', label: 'Telegram', svg: '/icons/telegram.svg', placeholder: 'https://t.me/username' },
  { value: 'whatsapp', label: 'WhatsApp', svg: '/icons/whatsapp.svg', placeholder: 'https://wa.me/phonenumber' },
  { value: 'signal-chat', label: 'Signal', svg: '/icons/signal-chat.svg', placeholder: 'https://signal.me/#u/username' },
  { value: 'skillsnap', label: 'SkillSnap', svg: '/icons/skillsnap.svg', placeholder: 'https://username.skillsnap.me' },
];

const GEN_TYPE_CONFIG: Record<GenerationType, { icon: React.ReactNode; className: string; tooltip: string }> = {
  manual: { icon: <Settings className="w-3.5 h-3.5 mr-1" />, className: 'bg-gray-200 text-gray-700 hover:bg-gray-300', tooltip: 'Manual: content appears on bespoke resumes exactly as written, 1:1' },
  embedding: { icon: <Brain className="w-3.5 h-3.5 mr-1" />, className: 'bg-purple-100 text-purple-700 hover:bg-purple-200', tooltip: 'AI Match: AI selects the most relevant items matched against the Job Description, without altering your content' },
  generate: { icon: <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />, className: 'bg-purple-200 text-purple-800 hover:bg-purple-300', tooltip: 'AI Generate: AI fully generates new content tailored to the Job Description — double-check the results!' },
  omit: { icon: <XCircle className="w-3.5 h-3.5 mr-1" />, className: 'bg-red-100 text-red-700 hover:bg-red-200', tooltip: 'Omit: this section will not appear on bespoke resumes' },
};

function Tip({ text, children }: { text: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => { timer.current = setTimeout(() => setShow(true), 400); }}
      onMouseLeave={() => { clearTimeout(timer.current); setShow(false); }}
    >
      {children}
      {show && (
        <span className="absolute z-[70] bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-950 rounded-lg shadow-lg whitespace-pre-line min-w-[280px] max-w-[420px] text-left pointer-events-none leading-relaxed">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-950" />
        </span>
      )}
    </span>
  );
}

const DEFAULT_GEN_TYPES: Record<SubcomponentType, GenerationType> = {
  contact: 'manual',
  summary: 'generate',
  skills: 'embedding',
  highlights: 'generate',
  experience: 'embedding',
  education: 'manual',
  awards: 'manual',
  presentations: 'manual',
  publications: 'manual',
  portfolios: 'manual',
  coverletter: 'generate',
  elevatorpitch: 'omit',
};

const emptyResume: ResumeJSON = {
  name: '',
  contact: [],
  summary: '',
  skills: [],
  highlights: [],
  experience: [],
  education: [],
  awards: [],
  presentations: [],
  publications: [],
  portfolios: [],
  summary_phrases: [],
};

/**
 * Normalize a recommendation from old "replacement" format to new "action/data" format.
 * Backwards compatible: if rec has "replacement" but no "action", convert it.
 */
function normalizeRec(rec: Recommendation): Recommendation {
  if (rec.replacement && !rec.action) {
    const field = rec.field;
    let data: unknown;
    if (field in rec.replacement) {
      data = rec.replacement[field];
    } else {
      const vals = Object.values(rec.replacement);
      data = vals.length > 0 ? vals[0] : null;
    }
    return { ...rec, action: 'replace', data };
  }
  // Ensure action defaults to replace
  if (!rec.action) {
    return { ...rec, action: 'replace' };
  }
  return rec;
}

/**
 * Parse a JSON path into segments.
 * "experience[0].roles[1].bullets[2]" -> [{key:"experience",idx:0}, {key:"roles",idx:1}, {key:"bullets",idx:2}]
 */
function parsePath(path: string): { key: string; idx?: number }[] {
  const segments: { key: string; idx?: number }[] = [];
  const parts = path.split('.');
  for (const part of parts) {
    const match = part.match(/^(\w+)\[(\d+)\]$/);
    if (match) {
      segments.push({ key: match[1], idx: parseInt(match[2], 10) });
    } else {
      segments.push({ key: part });
    }
  }
  return segments;
}

/**
 * Navigate to a target in resume data by path, returning { current, parent, parentKey }.
 */
function navigatePath(obj: any, segments: { key: string; idx?: number }[]): { current: any; parent: any; parentKey: string | number | undefined } {
  let current: any = obj;
  let parent: any = null;
  let parentKey: string | number | undefined;
  for (const seg of segments) {
    if (current == null) return { current: null, parent, parentKey };
    parent = current;
    parentKey = seg.key;
    current = current[seg.key];
    if (current == null) return { current: null, parent, parentKey };
    if (seg.idx !== undefined) {
      if (!Array.isArray(current) || seg.idx >= current.length) return { current: null, parent, parentKey };
      parent = current;
      parentKey = seg.idx;
      current = current[seg.idx];
    }
  }
  return { current, parent, parentKey };
}

/**
 * Apply a recommendation action (replace/append/remove) at the given JSON path.
 * path format: "contacts[0]", "experience[0].roles[0].bullets[1]", "skills[3]", "summary"
 */
function applyActionAtPath(resumeData: ResumeJSON, path: string, field: string, action: string, data: unknown): ResumeJSON {
  const updated = JSON.parse(JSON.stringify(resumeData)) as ResumeJSON;
  const segments = parsePath(path);
  const { current, parent, parentKey } = navigatePath(updated, segments);

  if (current == null && parent == null) return updated;

  if (action === 'replace') {
    if (current != null && typeof current === 'object' && !Array.isArray(current)) {
      // Object target: set the field directly
      current[field] = data;
    } else if (parent != null && parentKey !== undefined) {
      // Scalar or direct target: replace the value at parent
      parent[parentKey] = data;
    }
  } else if (action === 'append') {
    const dataArr = Array.isArray(data) ? data : [data];
    if (current != null && typeof current === 'object' && !Array.isArray(current)) {
      // Object target: append to the field's array
      const existing = Array.isArray(current[field]) ? current[field] as unknown[] : [];
      const merged = [...existing];
      for (const item of dataArr) {
        if (!merged.includes(item)) merged.push(item);
      }
      current[field] = merged;
    } else if (parent != null && parentKey !== undefined) {
      // Array target: append items, coercing scalars into objects when the array
      // already contains objects (e.g., skills items must be {title, tags}).
      if (Array.isArray(current)) {
        const sample = current.length > 0 ? current[0] : null;
        const targetIsObjArr = sample != null && typeof sample === 'object';
        const labelField = targetIsObjArr
          ? ('title' in (sample as object) ? 'title' : 'text' in (sample as object) ? 'text' : 'name')
          : null;
        const merged = [...current];
        for (const item of dataArr) {
          let toAdd: unknown = item;
          if (targetIsObjArr && labelField && typeof item === 'string') {
            toAdd = { [labelField]: item, tags: [] };
          }
          if (typeof toAdd === 'object') {
            merged.push(toAdd);  // objects: skip reference-equality dedup
          } else if (!merged.includes(toAdd)) {
            merged.push(toAdd);
          }
        }
        parent[parentKey] = merged;
      }
    }
  } else if (action === 'remove') {
    const dataArr = Array.isArray(data) ? data : [data];
    const removeSet = new Set(dataArr.map(String));
    if (current != null && typeof current === 'object' && !Array.isArray(current) && Array.isArray((current as any)[field])) {
      // Object target with array field: remove items from current[field] by stringified value
      (current as any)[field] = ((current as any)[field] as unknown[]).filter(
        (item: unknown) => !removeSet.has(String(item))
      );
    } else if (Array.isArray(current) && parent != null && parentKey !== undefined) {
      // Array target: remove matching items.
      // For arrays of strings, match by stringified value.
      // For arrays of objects, match by item[field] (e.g., skills filtered by title).
      parent[parentKey] = current.filter((item: unknown) => {
        if (item != null && typeof item === 'object' && field) {
          return !removeSet.has(String((item as any)[field]));
        }
        return !removeSet.has(String(item));
      });
    } else if (Array.isArray(parent) && typeof parentKey === 'number') {
      // Item-in-array target (e.g., path "skills[9]"): remove the whole item from its parent array.
      (parent as unknown[]).splice(parentKey, 1);
    }
  }

  return updated;
}


/** Bullet text editor with PDF line-break preview overlay. */
function BulletTextWithLineBreaks({ text, onChange }: { text: string; onChange: (text: string) => void }) {
  const [editing, setEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const lines = splitBulletIntoLines(text);
  const orphan = hasOrphan(lines);
  const multiLine = lines.length > 1;

  if (editing) {
    return (
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        rows={Math.max(2, lines.length + 1)}
        className="px-2 py-1 border border-primary-300 rounded resize-none ring-1 ring-primary-200"
        placeholder="Achievement or responsibility"
        style={{ fontFamily: RESUME_FONT, fontSize: '9.5pt', lineHeight: 1.45, width: RESUME_BULLET_WIDTH }}
        autoFocus
      />
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="px-2 py-1 border border-gray-200 rounded cursor-text hover:border-gray-300 min-h-[3em]"
      style={{ fontFamily: RESUME_FONT, fontSize: '9.5pt', lineHeight: 1.45, width: RESUME_BULLET_WIDTH }}
      title={orphan ? 'Last line has an orphaned word — consider editing to reflow' : `${lines.length} line${lines.length !== 1 ? 's' : ''} in PDF`}
    >
      {!text ? (
        <span className="text-gray-400">Achievement or responsibility</span>
      ) : (
        lines.map((line, i) => {
          const isLast = i === lines.length - 1;
          const isOrphanLine = isLast && orphan;
          return (
            <span key={i}>
              <span className={isOrphanLine ? 'bg-amber-100 text-amber-800 rounded px-0.5' : ''}>
                {line}
              </span>
              {!isLast && (
                <>
                  <span className="text-gray-300 text-xs mx-0.5 select-none">↵</span>
                  <br />
                </>
              )}
            </span>
          );
        })
      )}
      {multiLine && (
        <span className={`ml-1 text-xs select-none ${orphan ? 'text-amber-600' : 'text-gray-400'}`}>
          ({lines.length}L{orphan ? ' · orphan' : ''})
        </span>
      )}
    </div>
  );
}


export function ResumeEditor({ isOpen, onClose, onSuccess, resumename, onDeleteResume }: ResumeEditorProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Tag-generation status shown in the import notes box after an AI import.
  // 'generating' while the async tag pipeline runs, 'done' once tags land,
  // 'timeout' if it takes unusually long. Takes visual precedence over `warning`.
  const [tagGenPhase, setTagGenPhase] = useState<'idle' | 'generating' | 'done' | 'timeout'>('idle');
  const tagPollRef = useRef<{ cancelled: boolean } | null>(null);
  const [name, setName] = useState('');
  const [resume, setResume] = useState<ResumeJSON>(emptyResume);
  const [newSkill, setNewSkill] = useState('');
  const [newSummaryPhrase, setNewSummaryPhrase] = useState('');

  // New contact item state
  const [newContactIcon, setNewContactIcon] = useState<ContactItem['icon']>('email-at');
  const [newContactTitle, setNewContactTitle] = useState('');
  const [newContactUrl, setNewContactUrl] = useState('');
  const [newContactAutoFill, setNewContactAutoFill] = useState(false);
  const [editContactAutoFill, setEditContactAutoFill] = useState(false);
  
  // New education state
  const [newEduTitle, setNewEduTitle] = useState('');
  const [newEduOrg, setNewEduOrg] = useState('');
  const [newEduYear, setNewEduYear] = useState('');
  
  // New award state
  const [newAwardTitle, setNewAwardTitle] = useState('');
  const [newAwardIssuer, setNewAwardIssuer] = useState('');
  const [newAwardReward, setNewAwardReward] = useState('');
  const [newAwardYear, setNewAwardYear] = useState('');

  // New presentation state
  const [newPresentationTitle, setNewPresentationTitle] = useState('');
  const [newPresentationEvent, setNewPresentationEvent] = useState('');
  const [newPresentationYear, setNewPresentationYear] = useState('');

  // New publication state
  const [newPubTitle, setNewPubTitle] = useState('');
  const [newPubUrl, setNewPubUrl] = useState('');
  const [newPubYear, setNewPubYear] = useState('');

  // New portfolio state
  const [newPortTitle, setNewPortTitle] = useState('');
  const [newPortUrl, setNewPortUrl] = useState('');
  const [newPortYear, setNewPortYear] = useState('');

  // Experience editing state
  const [editingContactIndex, setEditingContactIndex] = useState<number | null>(null);
  const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null);
  const [editingSummaryPhraseIndex, setEditingSummaryPhraseIndex] = useState<number | null>(null);
  const [editingEducationIndex, setEditingEducationIndex] = useState<number | null>(null);
  const [editingAwardIndex, setEditingAwardIndex] = useState<number | null>(null);
  const [editingPresentationIndex, setEditingPresentationIndex] = useState<number | null>(null);
  const [editingPublicationIndex, setEditingPublicationIndex] = useState<number | null>(null);
  const [editingPortfolioIndex, setEditingPortfolioIndex] = useState<number | null>(null);
  const [editingCompanyIndex, setEditingCompanyIndex] = useState<number | null>(null);
  const [editingRoleIndex, setEditingRoleIndex] = useState<{ companyIdx: number; roleIdx: number } | null>(null);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Clone state
  const [cloning, setCloning] = useState(false);

  // Import state
  const [importing, setImporting] = useState(false);
  // Full-resume export state ('pdf' | 'text' while a request is in flight)
  const [exporting, setExporting] = useState<null | 'pdf' | 'text'>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // AI Review state
  const [reviewing, setReviewing] = useState(false);
  const [reviewComplete, setReviewComplete] = useState(false);
  const [editedReplacements, setEditedReplacements] = useState<Record<string, string>>({});
  const [showAiUpgradeModal, setShowAiUpgradeModal] = useState(false);

  // Master Resume URL state — always published (no opt-out)
  const [publishMasterResume, setPublishMasterResume] = useState(true);
  const [masterResumePath, setMasterResumePath] = useState('');
  const [otherResumePaths, setOtherResumePaths] = useState<{ resumename: string; path: string }[]>([]);

  // View menu dropdown state (HTML / PDF)
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!viewMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (viewMenuRef.current && !viewMenuRef.current.contains(e.target as Node)) {
        setViewMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [viewMenuOpen]);

  // Contact icon options — loaded from global settings, falls back to hardcoded defaults
  const [iconOptions, setIconOptions] = useState(DEFAULT_ICON_OPTIONS);

  // Section generation type state (syncs with Settings / Resume Sections)
  const [sectionGenTypes, setSectionGenTypes] = useState<Record<SubcomponentType, GenerationType>>({ ...DEFAULT_GEN_TYPES });

  // Fetch contact icon list from global settings on mount
  useEffect(() => {
    api.getPublicSetting('contact_icon_list')
      .then(data => {
        if (Array.isArray(data.setting_value) && data.setting_value.length > 0) {
          setIconOptions(data.setting_value);
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  // Cancel any in-flight tag-generation poll loop on unmount.
  useEffect(() => {
    return () => {
      if (tagPollRef.current) tagPollRef.current.cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError(null); // Clear any previous errors when modal opens
      loadSectionGenTypes();
      if (resumename) {
        loadResume();
      } else {
        setName('');
        setResume(emptyResume);
        setPublishMasterResume(true);
        setMasterResumePath('');
        setOtherResumePaths([]);
      }
    }
  }, [isOpen, resumename]);

  // ESC key to close modal (respects import state)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, importing]);

  // Click outside skill to close editing
  useEffect(() => {
    if (editingSkillIndex === null) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`[data-skill-index="${editingSkillIndex}"]`)) {
        setEditingSkillIndex(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [editingSkillIndex]);

  // Click outside summary phrase to close editing
  useEffect(() => {
    if (editingSummaryPhraseIndex === null) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`[data-phrase-index="${editingSummaryPhraseIndex}"]`)) {
        setEditingSummaryPhraseIndex(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [editingSummaryPhraseIndex]);

  async function loadSectionGenTypes() {
    try {
      const prefs = await api.getPreferences();
      const types: Record<SubcomponentType, GenerationType> = { ...DEFAULT_GEN_TYPES };
      const sections: SubcomponentType[] = ['contact', 'summary', 'skills', 'highlights', 'experience', 'education', 'awards', 'presentations', 'publications', 'portfolios', 'coverletter'];
      for (const s of sections) {
        const key = `default_gen_${s}` as keyof UserPreferences;
        if (prefs[key]) types[s] = prefs[key] as GenerationType;
      }
      setSectionGenTypes(types);
    } catch { /* use defaults */ }
  }

  function cycleSectionGenType(component: SubcomponentType) {
    const current = sectionGenTypes[component];
    const allowed = GENERATION_TYPE_RESTRICTIONS[component] || ['manual', 'embedding', 'generate', 'omit'];
    const idx = allowed.indexOf(current);
    const next = allowed[(idx + 1) % allowed.length];
    const updated = { ...sectionGenTypes, [component]: next };
    setSectionGenTypes(updated);
    // Persist to Settings / Resume Sections (fire-and-forget)
    const prefKey = `default_gen_${component}`;
    api.updatePreferences({ [prefKey]: next } as Partial<UserPreferences>).catch(() => {});
  }

  function renderGenTypeButton(component: SubcomponentType) {
    const genType = sectionGenTypes[component];
    const config = GEN_TYPE_CONFIG[genType];
    return (
      <button
        type="button"
        onClick={() => cycleSectionGenType(component)}
        data-help-target={`mainapp.resumes.resumeN.${component}.gentype`}
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${config.className} transition-colors`}
        title={`${config.tooltip}\n\nClick to change. This sets the default for ALL jobs generated from this resume.`}
      >
        {config.icon}
        {GENERATION_TYPE_LABELS[genType]}
      </button>
    );
  }

  // Normalize a loaded resumejson in place: fill missing sections, coerce
  // legacy shapes, and guarantee every taggable item has a tags array.
  function normalizeResumeJson(loadedResume: any) {
    // Ensure required structure exists with defaults
    if (!loadedResume.contact || !Array.isArray(loadedResume.contact)) {
      loadedResume.contact = [];
    }
    if (!loadedResume.experience) {
      loadedResume.experience = [];
    }
    if (!loadedResume.education) {
      loadedResume.education = [];
    }
    if (!loadedResume.skills) {
      loadedResume.skills = [];
    }
    if (!loadedResume.awards) {
      loadedResume.awards = [];
    }
    if (!loadedResume.presentations) {
      loadedResume.presentations = [];
    }
    if (!loadedResume.publications) {
      loadedResume.publications = [];
    }
    if (!loadedResume.portfolios) {
      loadedResume.portfolios = [];
    }
    if (!loadedResume.highlights) {
      loadedResume.highlights = [];
    }
    if (loadedResume.summary === undefined) {
      loadedResume.summary = '';
    }

    // Ensure skills are Skill objects
    if (loadedResume.skills.length > 0 && typeof loadedResume.skills[0] === 'string') {
      loadedResume.skills = (loadedResume.skills as any as string[]).map((s: string) => ({ title: s, tags: [] }));
    }
    // Ensure experience url is always an array
    if (loadedResume.experience) {
      for (const company of loadedResume.experience) {
        if (!Array.isArray(company.url)) {
          company.url = company.url ? [company.url as any] : [];
        }
      }
    }
    // Sanitize experience data — ensure all bullets have tags array
    if (loadedResume.experience) {
      for (const company of loadedResume.experience) {
        if (!company.roles) continue;
        for (const role of company.roles) {
          if (!role.bullets) role.bullets = [];
          role.bullets = role.bullets.map((b: any) => ({
            text: b.text || '',
            tags: Array.isArray(b.tags) ? b.tags : []
          }));
        }
      }
    }
    // Ensure education items have tags
    for (const edu of loadedResume.education) {
      if (!Array.isArray(edu.tags)) edu.tags = [];
    }
    // Ensure award items have tags
    for (const award of loadedResume.awards) {
      if (!Array.isArray(award.tags)) award.tags = [];
    }
    // Ensure presentation items have tags
    for (const pres of loadedResume.presentations) {
      if (!Array.isArray(pres.tags)) pres.tags = [];
    }
    // Ensure publication items have tags
    for (const pub of loadedResume.publications) {
      if (!Array.isArray(pub.tags)) pub.tags = [];
    }
    // Ensure portfolio items have tags
    for (const port of loadedResume.portfolios) {
      if (!Array.isArray(port.tags)) port.tags = [];
    }
    // Ensure summary_phrases exists and items have tags (backward compat)
    if (!Array.isArray(loadedResume.summary_phrases)) loadedResume.summary_phrases = [];
    for (const phrase of loadedResume.summary_phrases) {
      if (!Array.isArray(phrase.tags)) phrase.tags = [];
    }
    return loadedResume;
  }

  // Count tags across every taggable item — used to detect when the async
  // tag pipeline has written tags into the record (one-shot, not trickled).
  function countItemTags(rj: any): number {
    if (!rj) return 0;
    let n = 0;
    for (const company of rj.experience || []) {
      for (const role of company.roles || []) {
        for (const b of role.bullets || []) n += (b.tags?.length || 0);
      }
    }
    for (const s of rj.skills || []) n += (s.tags?.length || 0);
    for (const section of ['education', 'awards', 'presentations', 'publications', 'portfolios']) {
      for (const it of rj[section] || []) n += (it.tags?.length || 0);
    }
    return n;
  }

  // True if a save would trigger the backend to actually add tags — i.e. some
  // taggable item is below the per-item minimum the generator targets
  // (experience bullets: 10, all other sections: 5). Mirrors the backend's
  // ITEMS_TAG_COUNT_RANGES minimums so we only show the "generating" status and
  // poll when tags will really change (not on every routine edit-and-save).
  function needsTagGeneration(rj: any): boolean {
    if (!rj) return false;
    for (const company of rj.experience || []) {
      for (const role of company.roles || []) {
        for (const b of role.bullets || []) {
          if ((b.text || '').trim() && (b.tags?.length || 0) < 10) return true;
        }
      }
    }
    for (const s of rj.skills || []) {
      if ((s.title || '').trim() && (s.tags?.length || 0) < 5) return true;
    }
    for (const section of ['education', 'awards', 'presentations', 'publications', 'portfolios']) {
      for (const it of rj[section] || []) {
        if ((it.title || '').trim() && (it.tags?.length || 0) < 5) return true;
      }
    }
    return false;
  }

  // After an AI import auto-save, poll the record until the async tag pipeline
  // finishes (resume_tags === 'generated', or item-tag count grows past the
  // freshly-imported baseline), then load the tagged resume into the editor.
  function startTagGenPolling(resumeName: string, baselineTagCount: number) {
    // Cancel any prior poll loop.
    if (tagPollRef.current) tagPollRef.current.cancelled = true;
    const ctrl = { cancelled: false };
    tagPollRef.current = ctrl;
    const startedAt = Date.now();
    const MAX_WAIT_MS = 120000; // matches the gen_tags Lambda timeout
    const INTERVAL_MS = 4000;

    const tick = async () => {
      if (ctrl.cancelled) return;
      try {
        const data = await api.getResume(resumeName);
        if (ctrl.cancelled) return;
        const done = data.resume_tags === 'generated'
          || countItemTags(data.resumejson) > baselineTagCount;
        if (done) {
          const rj = normalizeResumeJson(data.resumejson);
          setResume(rj);
          setName(resumeName);
          setPublishMasterResume(true);
          setMasterResumePath(data.master_resume_path || '');
          setTagGenPhase('done');
          return;
        }
      } catch {
        // transient — keep polling until the timeout
      }
      if (ctrl.cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        setTagGenPhase('timeout');
        return;
      }
      setTimeout(tick, INTERVAL_MS);
    };
    setTimeout(tick, INTERVAL_MS);
  }

  // Pick a resume name not already in use, based on `base` (the imported
  // candidate name). Lets repeated imports create "Name", "Name (2)", ...
  // instead of failing the create with "already exists".
  async function deriveUniqueResumeName(base: string): Promise<string> {
    let existing: string[] = [];
    try {
      const data = await api.listResumes();
      existing = (data.resumes || []).map((r: any) => (r.resumename || '').toLowerCase());
    } catch {
      return base; // if the list fails, fall back to the base name
    }
    if (!existing.includes(base.toLowerCase())) return base;
    for (let i = 2; i < 100; i++) {
      const candidate = `${base} (${i})`;
      if (!existing.includes(candidate.toLowerCase())) return candidate;
    }
    return `${base} (${Date.now()})`;
  }

  async function loadResume() {
    if (!resumename) return;
    setLoading(true);
    try {
      const data = await api.getResume(resumename);
      setName(resumename);

      // Validate response structure
      if (!data || !data.resumejson) {
        console.error('Invalid resume data:', data);
        setError('Resume data is missing or corrupted. Please try again.');
        return;
      }

      const loadedResume = data.resumejson;

      normalizeResumeJson(loadedResume);
      setResume(loadedResume);

      // Load master resume settings — always published, no opt-out
      setPublishMasterResume(true);
      setMasterResumePath(data.master_resume_path || '');

      // Fetch other resumes to detect duplicate paths
      try {
        const listData = await api.listResumes();
        const others = (listData.resumes || [])
          .filter((r: any) => r.resumename !== resumename && r.master_resume_path !== undefined)
          .map((r: any) => ({ resumename: r.resumename, path: r.master_resume_path || '' }));
        setOtherResumePaths(others);
      } catch { /* non-critical */ }
    } catch (err: any) {
      console.error('Failed to load resume:', err);
      if (err.response?.status === 404) {
        setError(`Resume "${resumename}" not found. It may have been deleted.`);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(`Failed to load resume: ${err.message}`);
      } else {
        setError('Failed to load resume. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(closeOnSuccess: boolean = true, resumeOverride?: ResumeJSON, nameOverride?: string) {
    // resumeOverride lets callers (e.g. auto-save right after PDF import) save
    // freshly-loaded data without waiting for the setResume state commit.
    // nameOverride lets the import auto-save name a brand-new resume (whose
    // name field is still empty) without waiting for the setName commit.
    const resumeToSave = resumeOverride ?? resume;
    const savedName = (nameOverride ?? name).trim();
    if (!savedName) {
      setError('Resume name is required');
      return;
    }
    if (!resumeToSave.name) {
      setError('Your full professional name is required');
      return;
    }
    if (nameOverride && nameOverride.trim() && nameOverride.trim() !== name) {
      setName(nameOverride.trim());
    }

    setSaving(true);
    setError(null);
    try {
      if (resumename) {
        // Pass new name if it changed (for rename support)
        await api.updateResume(
          resumename, resumeToSave,
          savedName !== resumename ? savedName : undefined,
          masterResumePath,
          publishMasterResume,
        );
      } else {
        await api.createResume(savedName, resumeToSave, masterResumePath, publishMasterResume);
      }
      // Tell the parent what was saved. The parent refreshes its lists AND
      // sets its editing-resume state to savedName, so a brand-new resume
      // transitions to edit-mode (prop becomes defined) without the modal
      // closing — a subsequent Save then updates instead of creating a dup.
      onSuccess(savedName);
      if (closeOnSuccess) {
        onClose();
      } else if (needsTagGeneration(resumeToSave)) {
        // Keep-open save where the backend will (re)generate tags. Show the
        // status and poll until tags land, then load the tagged record — this
        // is what makes freshly-imported / under-tagged resumes show their
        // tags without a close/reopen. Works for create, rename, and edit.
        setWarning(null);
        setTagGenPhase('generating');
        startTagGenPolling(savedName, countItemTags(resumeToSave));
      } else if (resumename && savedName === resumename) {
        // Keep-open save in edit mode with no rename and nothing to generate:
        // reload in place so any other server-side changes appear. (Create and
        // rename cases reload via the prop-change load effect.)
        await loadResume();
      }
    } catch (err: any) {
      // Show specific error message if available
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to save resume');
      }
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // AI Review functions
  const allRecs = resume.recommendations || [];
  const hasReviewedBefore = allRecs.length > 0;
  const pendingRecs = allRecs.filter(r => r.status === 'pending');
  const hasRecommendations = pendingRecs.length > 0;

  // Per-section recommendation counts for top summary
  function getRecSectionCounts(): { section: string; count: number }[] {
    const counts: Record<string, number> = {};
    for (const rec of pendingRecs) {
      const section = rec.path.split('[')[0].split('.')[0];
      counts[section] = (counts[section] || 0) + 1;
    }
    return Object.entries(counts).map(([section, count]) => ({ section, count }));
  }

  async function handleAIReview() {
    if (!resumename) {
      setError('Save the resume first before running AI Review');
      return;
    }
    // Gate AI features for restricted user statuses
    try {
      const prefs = await api.getPreferences();
      const restrictedStatuses = ['trial', 'hibernating', 'cancelled', 'expired'];
      if (prefs.status && restrictedStatuses.includes(prefs.status)) {
        setShowAiUpgradeModal(true);
        return;
      }
    } catch {
      // If we can't check, allow the request (backend will enforce)
    }
    setReviewing(true);
    setError(null);
    try {
      const result = await api.reviewResume(resumename);
      const newRecs = result.recommendations || [];
      // Merge: keep existing dismissed/accepted, add new pending ones
      const existing = resume.recommendations || [];
      const kept = existing.filter(r => r.status === 'dismissed' || r.status === 'accepted');
      setResume({ ...resume, recommendations: [...kept, ...newRecs] });
      setReviewComplete(true);
      setEditedReplacements({});
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to generate AI review. Please try again.');
      }
      console.error(err);
    } finally {
      setReviewing(false);
    }
  }

  function itemLabel(item: unknown): string {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      const candidate = obj.title ?? obj.text ?? obj.name;
      if (typeof candidate === 'string') return candidate;
      return JSON.stringify(item);
    }
    return String(item);
  }

  function getDataDisplay(rec: Recommendation): string {
    const norm = normalizeRec(rec);
    const val = norm.data;
    if (val == null) return '';
    if (Array.isArray(val)) return val.map(itemLabel).join(', ');
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return itemLabel(val);
    return JSON.stringify(val);
  }

  function buildDataFromEdit(rec: Recommendation, editedText: string): unknown {
    const norm = normalizeRec(rec);
    const originalVal = norm.data;
    if (Array.isArray(originalVal)) {
      const labels = editedText.split(',').map(s => s.trim()).filter(Boolean);
      // If original items were objects, preserve their structure and update the label field.
      const first = originalVal.length > 0 ? originalVal[0] : null;
      if (first != null && typeof first === 'object') {
        const firstObj = first as Record<string, unknown>;
        const labelField = 'title' in firstObj ? 'title' : 'text' in firstObj ? 'text' : 'name';
        return labels.map((label, i) => {
          const orig = originalVal[i];
          if (orig != null && typeof orig === 'object') {
            return { ...(orig as Record<string, unknown>), [labelField]: label };
          }
          return { [labelField]: label, tags: [] };
        });
      }
      return labels;
    }
    return editedText;
  }

  function applyRecommendation(rec: Recommendation) {
    const norm = normalizeRec(rec);
    const editedText = editedReplacements[rec.id];
    const data = editedText !== undefined ? buildDataFromEdit(rec, editedText) : norm.data;
    const updated = applyActionAtPath(resume, norm.path, norm.field, norm.action, data);
    const updatedRecs = (updated.recommendations || []).map(r =>
      r.id === rec.id ? { ...r, status: 'accepted' as const } : r
    );
    setResume({ ...updated, recommendations: updatedRecs });
    setEditedReplacements(prev => { const next = {...prev}; delete next[rec.id]; return next; });
  }

  function dismissRecommendation(recId: string) {
    const updatedRecs = (resume.recommendations || []).map(r =>
      r.id === recId ? { ...r, status: 'dismissed' as const } : r
    );
    setResume({ ...resume, recommendations: updatedRecs });
    setEditedReplacements(prev => { const next = {...prev}; delete next[recId]; return next; });
  }

  function renderInlineRecs(path: string) {
    const matching = pendingRecs.filter(r => r.path === path);
    if (matching.length === 0) return null;
    const actionColors: Record<string, string> = {
      replace: 'bg-blue-100 text-blue-700',
      append: 'bg-emerald-100 text-emerald-700',
      remove: 'bg-red-100 text-red-700',
    };
    return (
      <>
        {matching.map(rec => {
          const norm = normalizeRec(rec);
          const displayVal = editedReplacements[rec.id] ?? getDataDisplay(rec);
          const isMultiline = rec.field === 'text' || rec.field === 'summary' || displayVal.length > 80;
          const isRemove = norm.action === 'remove';
          return (
            <div key={rec.id} className="ml-6 mt-0.5 mb-1 relative">
              {/* Arrow pointing up to the element above */}
              <div className="absolute -top-1 left-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-purple-200" />
              <div className="border border-purple-200 bg-purple-50 rounded pl-3 pr-2 py-2">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-purple-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`inline-block px-1.5 py-0 text-[10px] font-medium rounded ${actionColors[norm.action] || actionColors.replace}`}>
                        {norm.action}
                      </span>
                      <p className="text-xs text-purple-700">{rec.desc}</p>
                    </div>
                    {isRemove ? (
                      <p className="text-xs text-red-600 line-through px-2 py-1">{displayVal}</p>
                    ) : isMultiline ? (
                      <textarea
                        value={displayVal}
                        onChange={e => setEditedReplacements(prev => ({...prev, [rec.id]: e.target.value}))}
                        rows={rec.field === 'summary' || rec.path === 'summary' ? 5 : 2}
                        className="w-full px-2 py-1 border border-purple-200 rounded text-xs bg-white resize-none"
                      />
                    ) : (
                      <input
                        type="text"
                        value={displayVal}
                        onChange={e => setEditedReplacements(prev => ({...prev, [rec.id]: e.target.value}))}
                        className="w-full px-2 py-1 border border-purple-200 rounded text-xs bg-white"
                      />
                    )}
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => applyRecommendation(rec)}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        <Check className="w-3 h-3" /> Apply
                      </button>
                      <button
                        onClick={() => dismissRecommendation(rec.id)}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                      >
                        <XCircle className="w-3 h-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  }

  function applyAllRecommendations() {
    let updated = { ...resume };
    const recs = updated.recommendations || [];
    for (const rec of recs) {
      if (rec.status === 'pending') {
        const norm = normalizeRec(rec);
        updated = applyActionAtPath(updated, norm.path, norm.field, norm.action, norm.data);
      }
    }
    updated.recommendations = recs.map(r =>
      r.status === 'pending' ? { ...r, status: 'accepted' as const } : r
    );
    setResume(updated);
  }

  function dismissAllRecommendations() {
    const updatedRecs = (resume.recommendations || []).map(r =>
      r.status === 'pending' ? { ...r, status: 'dismissed' as const } : r
    );
    setResume({ ...resume, recommendations: updatedRecs });
  }

  // Skills functions
  function addSkill() {
    if (newSkill.trim()) {
      setResume({ ...resume, skills: [...resume.skills, { title: newSkill.trim(), tags: [] }] });
      setNewSkill('');
    }
  }

  function updateSkill(index: number, value: string) {
    const skills = [...resume.skills];
    skills[index] = { ...skills[index], title: value };
    setResume({ ...resume, skills });
  }

  function updateSkillTags(index: number, tags: string[]) {
    const skills = [...resume.skills];
    skills[index] = { ...skills[index], tags };
    setResume({ ...resume, skills });
  }

  function removeSkill(index: number) {
    setResume({ ...resume, skills: resume.skills.filter((_, i) => i !== index) });
  }

  // Summary Phrase functions
  function addSummaryPhrase() {
    if (newSummaryPhrase.trim()) {
      setResume({ ...resume, summary_phrases: [...(resume.summary_phrases || []), { text: newSummaryPhrase.trim(), tags: [] }] });
      setNewSummaryPhrase('');
    }
  }

  function updateSummaryPhraseText(index: number, value: string) {
    const phrases = [...(resume.summary_phrases || [])];
    phrases[index] = { ...phrases[index], text: value };
    setResume({ ...resume, summary_phrases: phrases });
  }

  function updateSummaryPhraseTags(index: number, tags: string[]) {
    const phrases = [...(resume.summary_phrases || [])];
    phrases[index] = { ...phrases[index], tags };
    setResume({ ...resume, summary_phrases: phrases });
  }

  function removeSummaryPhrase(index: number) {
    setResume({ ...resume, summary_phrases: (resume.summary_phrases || []).filter((_, i) => i !== index) });
  }

  // Contact functions
  function addContactItem() {
    if (newContactTitle.trim()) {
      const newItem: ContactItem = {
        icon: newContactIcon,
        title: newContactTitle.trim(),
        url: newContactUrl.trim(),
      };
      setResume({
        ...resume,
        contact: [...resume.contact, newItem]
      });
      setNewContactTitle('');
      setNewContactUrl('');
      setNewContactAutoFill(false);
    }
  }

  function updateContactItem(index: number, updates: Partial<ContactItem>) {
    const items = [...resume.contact];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, contact: items });
  }

  function removeContactItem(index: number) {
    setResume({
      ...resume,
      contact: resume.contact.filter((_, i) => i !== index)
    });
  }

  function moveContactItem(index: number, direction: 'up' | 'down') {
    const items = [...resume.contact];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, contact: items });
  }

  // Education functions
  function addEducation() {
    if (newEduTitle.trim() && newEduOrg.trim()) {
      const newEdu: Education = {
        title: newEduTitle.trim(),
        org: newEduOrg.trim(),
        year: newEduYear.trim(),
        tags: [],
      };
      setResume({ ...resume, education: [...resume.education, newEdu] });
      setNewEduTitle('');
      setNewEduOrg('');
      setNewEduYear('');
    }
  }

  function updateEducation(index: number, updates: Partial<Education>) {
    const items = [...resume.education];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, education: items });
  }

  function removeEducation(index: number) {
    setResume({ ...resume, education: resume.education.filter((_, i) => i !== index) });
  }

  function moveEducation(index: number, direction: 'up' | 'down') {
    const items = [...resume.education];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, education: items });
  }

  // Award functions
  function addAward() {
    if (newAwardTitle.trim()) {
      const newAwardItem: Award = {
        title: newAwardTitle.trim(),
        issuer: newAwardIssuer.trim(),
        reward: newAwardReward.trim(),
        year: newAwardYear.trim(),
        tags: [],
      };
      setResume({ ...resume, awards: [...resume.awards, newAwardItem] });
      setNewAwardTitle('');
      setNewAwardIssuer('');
      setNewAwardReward('');
      setNewAwardYear('');
    }
  }

  function updateAward(index: number, updates: Partial<Award>) {
    const items = [...resume.awards];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, awards: items });
  }

  function removeAward(index: number) {
    setResume({ ...resume, awards: resume.awards.filter((_, i) => i !== index) });
  }

  function moveAward(index: number, direction: 'up' | 'down') {
    const items = [...resume.awards];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, awards: items });
  }

  // Presentation functions
  function addPresentation() {
    if (newPresentationTitle.trim()) {
      const newPresentationItem: Presentation = {
        title: newPresentationTitle.trim(),
        event: newPresentationEvent.trim(),
        year: newPresentationYear.trim(),
        tags: [],
      };
      setResume({ ...resume, presentations: [...(resume.presentations || []), newPresentationItem] });
      setNewPresentationTitle('');
      setNewPresentationEvent('');
      setNewPresentationYear('');
    }
  }

  function updatePresentation(index: number, updates: Partial<Presentation>) {
    const items = [...(resume.presentations || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, presentations: items });
  }

  function removePresentation(index: number) {
    setResume({ ...resume, presentations: (resume.presentations || []).filter((_, i) => i !== index) });
  }

  function movePresentation(index: number, direction: 'up' | 'down') {
    const items = [...(resume.presentations || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, presentations: items });
  }

  // Publication functions
  function addPublication() {
    if (newPubTitle.trim()) {
      const newPubItem: Publication = {
        title: newPubTitle.trim(),
        url: newPubUrl.trim() || undefined,
        year: newPubYear.trim(),
        tags: [],
      };
      setResume({ ...resume, publications: [...(resume.publications || []), newPubItem] });
      setNewPubTitle('');
      setNewPubUrl('');
      setNewPubYear('');
    }
  }

  function updatePublication(index: number, updates: Partial<Publication>) {
    const items = [...(resume.publications || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, publications: items });
  }

  function removePublication(index: number) {
    setResume({ ...resume, publications: (resume.publications || []).filter((_, i) => i !== index) });
  }

  function movePublication(index: number, direction: 'up' | 'down') {
    const items = [...(resume.publications || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, publications: items });
  }

  // Portfolio functions
  function addPortfolio() {
    if (newPortTitle.trim()) {
      const newPortItem: Portfolio = {
        title: newPortTitle.trim(),
        url: newPortUrl.trim() || undefined,
        year: newPortYear.trim(),
        tags: [],
      };
      setResume({ ...resume, portfolios: [...(resume.portfolios || []), newPortItem] });
      setNewPortTitle('');
      setNewPortUrl('');
      setNewPortYear('');
    }
  }

  function updatePortfolio(index: number, updates: Partial<Portfolio>) {
    const items = [...(resume.portfolios || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, portfolios: items });
  }

  function removePortfolio(index: number) {
    setResume({ ...resume, portfolios: (resume.portfolios || []).filter((_, i) => i !== index) });
  }

  function movePortfolio(index: number, direction: 'up' | 'down') {
    const items = [...(resume.portfolios || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, portfolios: items });
  }

  // Experience functions
  function getExperience(): ExperienceCompany[] {
    return (resume.experience || []) as ExperienceCompany[];
  }

  function setExperience(exp: ExperienceCompany[]) {
    setResume({ ...resume, experience: exp });
  }

  function addCompany() {
    const newCompany: ExperienceCompany = {
      company: '',
      url: [],
      startDate: '',
      current: false,
      roles: []
    };
    setExperience([...getExperience(), newCompany]);
    setEditingCompanyIndex(getExperience().length);
  }

  function updateCompany(index: number, updates: Partial<ExperienceCompany>) {
    const exp = [...getExperience()];
    exp[index] = { ...exp[index], ...updates };
    setExperience(exp);
  }

  function removeCompany(index: number) {
    setExperience(getExperience().filter((_, i) => i !== index));
    setEditingCompanyIndex(null);
  }

  function moveCompany(index: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= exp.length) return;
    [exp[index], exp[newIndex]] = [exp[newIndex], exp[index]];
    setExperience(exp);
  }

  function addRole(companyIndex: number) {
    const exp = [...getExperience()];
    const newRole: ExperienceRole = {
      title: '',
      startDate: '',
      current: false,
      bullets: []
    };
    exp[companyIndex].roles = [...exp[companyIndex].roles, newRole];
    setExperience(exp);
    setEditingRoleIndex({ companyIdx: companyIndex, roleIdx: exp[companyIndex].roles.length - 1 });
  }

  function updateRole(companyIndex: number, roleIndex: number, updates: Partial<ExperienceRole>) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex] = { ...exp[companyIndex].roles[roleIndex], ...updates };
    setExperience(exp);
  }

  function removeRole(companyIndex: number, roleIndex: number) {
    const exp = [...getExperience()];
    exp[companyIndex].roles = exp[companyIndex].roles.filter((_, i) => i !== roleIndex);
    setExperience(exp);
    setEditingRoleIndex(null);
  }

  function moveRole(companyIndex: number, roleIndex: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const roles = [...exp[companyIndex].roles];
    const newIndex = direction === 'up' ? roleIndex - 1 : roleIndex + 1;
    if (newIndex < 0 || newIndex >= roles.length) return;
    [roles[roleIndex], roles[newIndex]] = [roles[newIndex], roles[roleIndex]];
    exp[companyIndex].roles = roles;
    setExperience(exp);
  }

  function addBullet(companyIndex: number, roleIndex: number) {
    const exp = [...getExperience()];
    const newBullet: ExperienceBullet = { text: '', tags: [] };
    exp[companyIndex].roles[roleIndex].bullets = [...exp[companyIndex].roles[roleIndex].bullets, newBullet];
    setExperience(exp);
  }

  function updateBullet(companyIndex: number, roleIndex: number, bulletIndex: number, updates: Partial<ExperienceBullet>) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
      ...exp[companyIndex].roles[roleIndex].bullets[bulletIndex],
      ...updates
    };
    setExperience(exp);
  }

  function removeBullet(companyIndex: number, roleIndex: number, bulletIndex: number) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex].bullets = exp[companyIndex].roles[roleIndex].bullets.filter((_, i) => i !== bulletIndex);
    setExperience(exp);
  }

  function moveBullet(companyIndex: number, roleIndex: number, bulletIndex: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const bullets = [...exp[companyIndex].roles[roleIndex].bullets];
    const newIndex = direction === 'up' ? bulletIndex - 1 : bulletIndex + 1;
    if (newIndex < 0 || newIndex >= bullets.length) return;
    [bullets[bulletIndex], bullets[newIndex]] = [bullets[newIndex], bullets[bulletIndex]];
    exp[companyIndex].roles[roleIndex].bullets = bullets;
    setExperience(exp);
  }

  function getIconSvg(iconName: ContactItem['icon']) {
    const iconConfig = iconOptions.find(i => i.value === iconName);
    return iconConfig?.svg || '/icons/globe.svg';
  }

  function getUrlPlaceholder(iconName: ContactItem['icon']) {
    const iconConfig = iconOptions.find(i => i.value === iconName);
    return iconConfig?.placeholder || 'https://example.com';
  }

  function autoFillContactUrl(icon: ContactItem['icon'], title: string): string | null {
    const trimmed = title.trim();
    if (!trimmed) return null;
    const contactPrefix: Record<string, string> = {
      'email-at': 'mailto:',
      'phone': 'tel:+',
      'phone-volume': 'tel:+',
      'linkedin': 'https://www.linkedin.com/in/',
      'x-twitter': 'https://x.com/',
      'github': 'https://github.com/',
      'github-square': 'https://github.com/',
      'discord': 'https://discord.com/users/',
      'telegram': 'https://t.me/',
      'signal-chat': 'https://signal.me/#u/',
    };
    const prefix = contactPrefix[icon];
    if (!prefix) return null;
    if (icon === 'phone' || icon === 'phone-volume') {
      const digits = trimmed.replace(/\D/g, '');
      if (!digits) return null;
      const normalized = (digits.length === 10 && /^[2-9]/.test(digits)) ? `1${digits}` : digits;
      return `tel:+${normalized}`;
    }
    if (icon === 'email-at') {
      return `mailto:${trimmed}`;
    }
    return `${prefix}${trimmed}`;
  }

  // Import functions
  function handleLoadFromFileClick() {
    fileInputRef.current?.click();
  }

  async function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input so the same file can be selected again
    event.target.value = '';

    setImporting(true);
    setError(null);
    setWarning(null);
    setTagGenPhase('idle');
    if (tagPollRef.current) tagPollRef.current.cancelled = true;

    try {
      const result = await importResumeFile(file);

      // Populate form with imported data
      setResume(result.resumejson);

      // Build the friendly import-notes message (shown only when we are NOT
      // about to kick off tag generation; otherwise the tag-gen status box
      // replaces it).
      let importNotes: string | null = null;
      if (result.warnings && result.warnings.length > 0) {
        const friendly = result.warnings.map(w => {
          // Rephrase Schema warnings into plain language
          const schemaMatch = w.match(/^Schema: (\w+)\[0\]: missing required field '(\w+)'$/);
          if (schemaMatch) return `Did not find a ${schemaMatch[1]} section`;
          if (w.includes('processed by AI')) return 'PDF content was processed by AI — please verify accuracy';
          return w;
        });
        importNotes = `Import completed successfully, with a few notes:\n${friendly.map(f => `• ${f}`).join('\n')}`;
      }

      // R7: kick off tag generation immediately by auto-saving (without closing).
      // Pass the freshly imported data explicitly so we don't depend on the
      // setResume state commit above. The async tag pipeline picks up from the
      // save. A brand-new resume normally has an empty name field, which used to
      // silently skip the auto-save — so tags never generated and no status
      // showed. When this is a NEW resume with no name, default it to a unique
      // name based on the imported candidate name so generation always runs.
      const candidateName = (result.resumejson?.name || '').trim();
      let effectiveName = name.trim();
      if (!effectiveName && !resumename && candidateName) {
        effectiveName = await deriveUniqueResumeName(candidateName);
      }
      // Auto-save kicks off tag generation. handleSave shows the tag-gen status
      // and polls until tags land (see its keep-open branch), so the imported
      // resume's tags appear without a manual close/reopen.
      const willSave = !!(effectiveName && result.resumejson?.name);
      if (willSave) {
        if (!name.trim()) setName(effectiveName);
        handleSave(false, result.resumejson, effectiveName).catch((e) => {
          console.warn('Auto-save after import failed:', e);
          if (importNotes) setWarning(importNotes);
        });
      } else if (importNotes) {
        // Could not auto-save (e.g. the importer didn't extract a name) — show
        // the import notes so the user knows to review and save manually.
        setWarning(importNotes);
      }
    } catch (err) {
      // Preserve existing form data on error (Requirement 6.6)
      // Handle specific error types with user-friendly messages (Requirements 8.1-8.5)
      if (err instanceof ImportValidationError) {
        setError(err.message);
      } else if (err instanceof ImportUploadError) {
        setError(err.message);
      } else if (err instanceof ImportProcessError) {
        setError(err.message);
      } else if (err instanceof ImportAuthError) {
        setError(err.message);
      } else if (err instanceof ImportTimeoutError) {
        setError(err.message);
      } else {
        // Generic fallback error (Requirement 8.4)
        setError('Could not read file. Please ensure it\'s a valid YAML, JSON, or PDF file.');
        // Log detailed error for debugging (Requirement 8.5)
        console.error('Import error:', err);
      }
    } finally {
      setImporting(false);
    }
  }

  function handleDownloadTemplate() {
    // Check if resume has real data (not just empty/default values)
    const hasRealData = resume.name ||
                        resume.skills.length > 0 ||
                        resume.summary ||
                        getExperience().length > 0 ||
                        resume.education.length > 0 ||
                        resume.awards.length > 0 ||
                        (resume.presentations || []).length > 0 ||
                        (resume.publications || []).length > 0 ||
                        (resume.portfolios || []).length > 0 ||
                        resume.contact.length > 0;

    if (!hasRealData) {
      // No real data - download the template
      const link = document.createElement('a');
      link.href = '/skillsnap-resume-template.yaml';
      link.download = 'skillsnap-resume-template.yaml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Convert resume data to YAML format
    const yamlData: Record<string, unknown> = {
      name: resume.name || 'Your Full Name',
    };

    // Add summary
    if (resume.summary) {
      yamlData.summary = resume.summary;
    }

    // Convert contacts (unified format: title/icon/url)
    if (resume.contact.length > 0) {
      yamlData.contacts = resume.contact.map(item => ({
          title: item.title,
          icon: item.icon,
          url: item.url,
        }));
    }

    // Add skills (include tags for AI matching)
    if (resume.skills.length > 0) {
      yamlData.skills = resume.skills.map(s => ({
        title: s.title,
        tags: s.tags || []
      }));
    }

    // Convert experience (canonical format: startDate/endDate/current/title)
    if (getExperience().length > 0) {
      yamlData.experience = getExperience().map(company => {
        const expEntry: Record<string, unknown> = {
          company: company.company,
        };
        if (company.url && company.url.length > 0) expEntry.url = company.url;
        if (company.employees) expEntry.employees = company.employees;
        expEntry.startDate = company.startDate || '';
        expEntry.endDate = company.endDate || '';
        expEntry.current = company.current || false;

        if (company.location) expEntry.location = company.location;
        if (company.description) expEntry.description = company.description;

        // Convert roles
        expEntry.roles = company.roles.map(role => {
          const roleEntry: Record<string, unknown> = {
            title: role.title,
            startDate: role.startDate || '',
            endDate: role.endDate || '',
            current: role.current || false,
          };

          if (role.location) roleEntry.location = role.location;

          roleEntry.bullets = role.bullets.map(bullet => ({
            text: bullet.text,
            tags: bullet.tags || []
          }));

          return roleEntry;
        });

        return expEntry;
      });
    }

    // Convert education (canonical field names + tags)
    if (resume.education.length > 0) {
      yamlData.education = resume.education.map(edu => ({
        title: edu.title,
        org: edu.org,
        url: edu.url || '',
        year: edu.year || '',
        tags: edu.tags || []
      }));
    }

    // Convert awards (canonical field names + tags)
    if (resume.awards.length > 0) {
      yamlData.awards = resume.awards.map(award => ({
        title: award.title,
        issuer: award.issuer || '',
        reward: award.reward || '',
        url: award.url || '',
        year: award.year || '',
        tags: award.tags || []
      }));
    }

    // Convert presentations (add tags)
    if ((resume.presentations || []).length > 0) {
      yamlData.presentations = (resume.presentations || []).map(presentation => ({
        title: presentation.title,
        event: presentation.event || '',
        url: presentation.url || '',
        year: presentation.year || '',
        tags: presentation.tags || []
      }));
    }

    // Convert publications (add tags)
    if ((resume.publications || []).length > 0) {
      yamlData.publications = (resume.publications || []).map(pub => ({
        title: pub.title,
        url: pub.url || '',
        year: pub.year || '',
        tags: pub.tags || []
      }));
    }

    // Convert portfolios (add tags)
    if ((resume.portfolios || []).length > 0) {
      yamlData.portfolios = (resume.portfolios || []).map(port => ({
        title: port.title,
        url: port.url || '',
        year: port.year || '',
        tags: port.tags || []
      }));
    }

    // Convert to YAML string with header comment
    const yamlHeader = `# SkillSnap Resume Export
# Generated from: ${name || 'Untitled Resume'}
# Date: ${new Date().toISOString().split('T')[0]}
# Import this file using "Load from File" in SkillSnap.

`;
    const yamlContent = yamlHeader + yaml.dump(yamlData, {
      indent: 2,
      lineWidth: -1, // Don't wrap lines
      noRefs: true,
      quotingType: '"',
      forceQuotes: false
    });

    // Create and download the file
    const blob = new Blob([yamlContent], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = name ? `${name.replace(/[^a-zA-Z0-9]/g, '_')}.yaml` : 'resume.yaml';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Export the full, unfiltered resume (all sections / all items, no page-fit
  // pruning) as a PDF or plain-text file, then open it in a new tab.
  async function handleExportFull(format: 'pdf' | 'text') {
    setExporting(format);
    setError(null);
    setWarning(null);
    try {
      const { publicUrl } = await api.exportFullResume(format, resumename || name.trim(), resume);
      window.open(publicUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || `Failed to export ${format === 'pdf' ? 'PDF' : 'text'}.`;
      setError(msg);
    } finally {
      setExporting(null);
    }
  }

  // Prevent modal close during import
  function handleClose() {
    if (importing) return;
    if (tagPollRef.current) tagPollRef.current.cancelled = true;
    setTagGenPhase('idle');
    onClose();
  }

  async function handleClone() {
    if (!resumename) return;
    setCloning(true);
    setError(null);
    try {
      // Generate clone name: "Resume Name (1)", or increment if that exists
      const baseName = name || resumename;
      const match = baseName.match(/^(.*)\s*\((\d+)\)$/);
      let cloneName: string;
      if (match) {
        cloneName = `${match[1]} (${parseInt(match[2], 10) + 1})`;
      } else {
        cloneName = `${baseName} (1)`;
      }
      await api.createResume(cloneName, resume);
      onClose();
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to clone resume';
      setError(msg);
    } finally {
      setCloning(false);
    }
  }

  async function handleDeleteResume() {
    if (!resumename || !onDeleteResume) return;
    setDeleting(true);
    try {
      await onDeleteResume(resumename);
      setShowDeleteConfirm(false);
      onClose();
      onSuccess();
    } catch (err) {
      console.error('Failed to delete resume:', err);
      setError('Failed to delete resume');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label={resumename ? 'Edit Resume' : 'Create Resume'} data-help-target="mainapp.resumes.resumeN">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-[986px] max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">
              {resumename ? 'Edit Resume' : 'Create Resume'}
            </h2>
            {resumename && (
              <button
                onClick={handleClone}
                disabled={cloning || saving || importing || loading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                title="Clone this resume"
              >
                {cloning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                Clone
              </button>
            )}
          </div>
          <button onClick={handleClose} disabled={importing} className="p-1 hover:bg-gray-100 rounded disabled:opacity-50" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Import Progress Overlay */}
        {importing && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-10" role="status" aria-label="Analyzing resume file">
            <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
            <p className="text-gray-700 font-medium">Analyzing resume file...</p>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".yaml,.yml,.json,.pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {loading ? (
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading resume">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {tagGenPhase === 'generating' ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm flex items-start gap-2">
                <Loader2 className="w-4 h-4 mt-0.5 flex-shrink-0 animate-spin" />
                <span>
                  Tag generation has started — this usually takes about 60 seconds.
                  Your tags will appear here automatically when they're ready; you can keep reviewing in the meantime.
                  <br />
                  <span className="text-yellow-700">Imported content was processed by AI — please verify accuracy.</span>
                </span>
              </div>
            ) : tagGenPhase === 'done' ? (
              <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded-lg text-sm flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Tag generation complete — tags have been added to your resume.
                  <br />
                  <span className="text-green-700">Imported content was processed by AI — please verify accuracy.</span>
                </span>
              </div>
            ) : tagGenPhase === 'timeout' ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm">
                Tag generation is taking longer than usual. Close and reopen this resume in a moment to see the tags.
              </div>
            ) : warning ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm whitespace-pre-line">{warning}</div>
            ) : null}
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
            )}

            {/* Resume Name with Import Buttons */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-1">
                Resume Name *
              </label>
              <div className="flex gap-2 items-center flex-wrap">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-help-target="mainapp.resumes.resumeN.resumename"
                  className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="My Professional Resume"
                  disabled={importing}
                  autoComplete="off"
                />
                {(() => {
                  const hasResumeData = !!(resume.name || resume.skills.length > 0 || resume.summary || getExperience().length > 0);
                  const busy = importing || loading || exporting !== null;
                  return (
                    <>
                      <button
                        onClick={handleLoadFromFileClick}
                        disabled={busy}
                        data-help-target="mainapp.resumes.resumeN.loadfrompdf"
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        title="Load from PDF"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Load from PDF</span>
                      </button>
                      <button
                        onClick={() => handleExportFull('pdf')}
                        disabled={busy || !hasResumeData}
                        data-help-target="mainapp.resumes.resumeN.exporttopdf"
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        title="Export the full resume (all sections, all items) as a PDF"
                      >
                        {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        <span>Export to PDF</span>
                      </button>
                      <button
                        onClick={() => handleExportFull('text')}
                        disabled={busy || !hasResumeData}
                        data-help-target="mainapp.resumes.resumeN.exporttotext"
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        title="Export the full resume (all sections, all items) as a plain-text file"
                      >
                        {exporting === 'text' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileType className="w-4 h-4" />}
                        <span>Export to Text</span>
                      </button>
                      <button
                        onClick={handleDownloadTemplate}
                        disabled={busy}
                        data-help-target="mainapp.resumes.resumeN.exporttodatafile"
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        title={hasResumeData ? "Export resume data as a YAML datafile" : "Download Template"}
                      >
                        <Download className="w-4 h-4" />
                        <span>{hasResumeData ? "Export to Datafile" : "Download Template"}</span>
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* AI Review banner + button */}
            {resumename && (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  {(reviewComplete || hasReviewedBefore) && (
                    <div className={`flex-1 border rounded-lg p-3 ${hasRecommendations ? 'border-purple-200 bg-purple-50' : 'border-green-200 bg-green-50'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Sparkles className={`w-4 h-4 ${hasRecommendations ? 'text-purple-600' : 'text-green-600'}`} />
                          <span className={`text-sm font-medium ${hasRecommendations ? 'text-purple-800' : 'text-green-800'}`}>
                            {hasRecommendations
                              ? `${pendingRecs.length} pending recommendation${pendingRecs.length !== 1 ? 's' : ''}. Scroll down to review inline recommendations.`
                              : 'All recommendations have been implemented!'}
                          </span>
                        </div>
                        {hasRecommendations && (
                          <div className="flex gap-2">
                            <button
                              onClick={applyAllRecommendations}
                              className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                            >
                              Apply All
                            </button>
                            <button
                              onClick={dismissAllRecommendations}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                            >
                              Dismiss All
                            </button>
                          </div>
                        )}
                      </div>
                      {hasRecommendations && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {getRecSectionCounts().map(({ section, count }) => (
                            <span key={section} className="text-xs bg-white border border-purple-200 text-purple-700 px-2 py-0.5 rounded">
                              {section}: {count}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className={`text-xs mt-2 ${hasRecommendations ? 'text-purple-600' : 'text-green-600'}`}>
                        Remember: this resume will be customized per job, based on the job description. So while it&apos;s important to get a solid foundation, further AI customization is applied on top of this &quot;core&quot; resume.
                      </p>
                    </div>
                  )}
                  <button
                    onClick={handleAIReview}
                    disabled={reviewing || importing || loading || saving}
                    data-help-target="mainapp.resumes.resumeN.aireview"
                    className={`flex items-center gap-2 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap ${
                      (reviewComplete || hasReviewedBefore) && !reviewing ? 'px-5 bg-green-100 text-green-700 hover:bg-green-200' : 'px-3 bg-purple-100 text-purple-700 hover:bg-purple-200'
                    }`}
                    title="AI Resume Review"
                  >
                    {reviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : (reviewComplete || hasReviewedBefore) ? <Check className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                    {reviewing ? (
                      <span>Reviewing...</span>
                    ) : (reviewComplete || hasReviewedBefore) ? (
                      <span className="flex flex-col items-start leading-tight">
                        <span className="whitespace-nowrap">AI Review Complete!</span>
                        <span className="text-[10px] opacity-70">(click to run again)</span>
                      </span>
                    ) : (
                      <span>AI Review</span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Master Resume URL — always published, no opt-out */}
            <div className="space-y-2">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  This Resume&apos;s Master URL
                </label>
                <div className="flex items-center gap-0">
                  <span className="inline-flex items-center px-3 py-2 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-500 text-sm select-all">
                    https://{user?.username?.toLowerCase() || 'your_name'}.skillsnap.me/
                  </span>
                  <input
                    type="text"
                    value={masterResumePath}
                    onChange={(e) => {
                      // Only allow URL-safe characters
                      const val = e.target.value.replace(/[^a-zA-Z0-9_\-]/g, '');
                      setMasterResumePath(val);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg text-sm"
                    placeholder="(optional path, e.g. solution_engineer)"
                  />
                  {resumename && user?.username && (
                    <a
                      href={`https://${user.username.toLowerCase()}.skillsnap.me/${masterResumePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 ml-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm whitespace-nowrap"
                      title="View Resume"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>View Resume</span>
                    </a>
                  )}
                </div>
                {/* Duplicate path warning */}
                {(() => {
                  const normalizedPath = masterResumePath.toLowerCase();
                  const duplicate = otherResumePaths.find(
                    (r) => (r.path || '').toLowerCase() === normalizedPath
                  );
                  if (duplicate) {
                    return (
                      <div className="flex items-center gap-1 text-xs text-amber-600">
                        <AlertTriangle className="w-3 h-3" />
                        <span>
                          Resume &quot;{duplicate.resumename}&quot; also uses this path. Most recent save wins &mdash; only one will be displayed.
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
                {!resumename && (
                  <p className="text-xs text-gray-500 italic">
                    Save this resume once before you can view the resume URL.
                  </p>
                )}
                {masterResumePath === '' && resumename && (
                  <p className="text-xs text-gray-500">
                    Leaving the path blank will publish at the root: https://{user?.username?.toLowerCase() || 'your_name'}.skillsnap.me
                  </p>
                )}
              </div>
            </div>

            {/* Full Professional Name */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-1">
                Your Full Professional Name *
              </label>
              <input
                type="text"
                value={resume.name}
                onChange={(e) => setResume({
                  ...resume,
                  name: e.target.value
                })}
                data-help-target="mainapp.resumes.resumeN.profname"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="John Doe"
                autoComplete="off"
              />
            </div>

            <hr className="border-gray-200" />

            {/* Contact Information */}
            <div data-help-target="mainapp.resumes.resumeN.contact" className={sectionGenTypes.contact === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-bold text-gray-700">Contact Information</label>
                {renderGenTypeButton('contact')}
              </div>
              <div className="space-y-2 mb-3">
                {resume.contact.map((item, i) => (
                  <div key={i}>
                    <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${item.include === '0' ? ' opacity-40' : ''}`}>
                      <input
                        type="checkbox"
                        checked={item.include !== '0'}
                        onChange={(e) => updateContactItem(i, { include: e.target.checked ? '1' : '0' })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                        title={item.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                      />
                      {editingContactIndex === i ? (
                        <div className="flex-1 flex gap-2 items-center">
                          <div className="relative w-20">
                            <select value={item.icon} onChange={(e) => updateContactItem(i, { icon: e.target.value as ContactItem['icon'] })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm appearance-none pl-7">
                              {iconOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                            </select>
                            <img src={getIconSvg(item.icon)} alt="" className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>
                          <input type="text" value={item.title} onFocus={() => setEditContactAutoFill(!item.url)} onChange={(e) => { const updates: Partial<ContactItem> = { title: e.target.value }; if (editContactAutoFill) { const auto = autoFillContactUrl(item.icon, e.target.value); if (auto) updates.url = auto; } updateContactItem(i, updates); }} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" autoComplete="off" />
                          <input type="text" value={item.url || ''} onChange={(e) => { setEditContactAutoFill(false); updateContactItem(i, { url: e.target.value }); }} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL" autoComplete="off" />
                        </div>
                      ) : (
                        <>
                          <img src={getIconSvg(item.icon)} alt="" className="w-4 h-4" />
                          <span className="flex-1 text-sm truncate">
                            {item.title}
                            {item.url && <span className="text-gray-500 ml-2 text-xs">({item.url})</span>}
                          </span>
                        </>
                      )}
                      <button onClick={() => { setEditingContactIndex(editingContactIndex === i ? null : i); setEditContactAutoFill(false); }} className="text-gray-500 hover:text-gray-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveContactItem(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveContactItem(i, 'down')} disabled={i === resume.contact.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeContactItem(i)} className="text-gray-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {renderInlineRecs(`contacts[${i}]`)}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <div className="w-20">
                  <label className="block text-xs text-gray-500 mb-1">Icon</label>
                  <div className="relative">
                    <select
                      value={newContactIcon}
                      onChange={(e) => setNewContactIcon(e.target.value as ContactItem['icon'])}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm appearance-none pl-8"
                    >
                      {iconOptions.map(i => (
                        <option key={i.value} value={i.value}>{i.label}</option>
                      ))}
                    </select>
                    <img src={getIconSvg(newContactIcon)} alt="" className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Title</label>
                  <input type="text" value={newContactTitle} onFocus={() => setNewContactAutoFill(!newContactUrl)} onChange={(e) => { setNewContactTitle(e.target.value); if (newContactAutoFill) { const auto = autoFillContactUrl(newContactIcon, e.target.value); if (auto) setNewContactUrl(auto); } }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="name displayed" autoComplete="off" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">URL / Link</label>
                  <input type="text" value={newContactUrl} onChange={(e) => { setNewContactAutoFill(false); setNewContactUrl(e.target.value); }} onKeyDown={(e) => e.key === 'Enter' && addContactItem()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder={getUrlPlaceholder(newContactIcon)} autoComplete="off" />
                </div>
                <button onClick={addContactItem} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Summary */}
            <div data-help-target="mainapp.resumes.resumeN.summary" className={sectionGenTypes.summary === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-base font-bold text-gray-700">Summary</label>
                {renderGenTypeButton('summary')}
              </div>
              <textarea
                rows={5}
                value={resume.summary}
                onChange={(e) => setResume({ ...resume, summary: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
                style={{ fontFamily: RESUME_FONT, fontSize: '10.5pt', lineHeight: 1.45, width: RESUME_WIDTH + 26 }}
                placeholder="Brief professional summary..."
              />
              {renderInlineRecs('summary')}
            </div>

            {/* Summary Phrases */}
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-gray-600">Summary Phrases</label>
                <Tip text={"Summary Phrases let you define consistent phrasing that will be selected as a unit when building the summary section.\n\n• Phrases will be used verbatim, so take care while crafting the content\n• Completely optional — the summary can be generated without phrases\n• Phrases are ranked by tag relevance; low-match phrases are excluded automatically"}>
                  <span className="text-gray-400 hover:text-gray-600 cursor-help">
                    <HelpCircle className="w-3.5 h-3.5" />
                  </span>
                </Tip>
              </div>
              {(resume.summary_phrases || []).length > 0 && (
                <div className="space-y-2 mb-2">
                  {(resume.summary_phrases || []).map((phrase, i) => (
                    <div key={i} data-phrase-index={i} className={`flex items-start gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm border border-gray-100${phrase.include === '0' ? ' opacity-40' : ''}`}>
                      <input
                        type="checkbox"
                        checked={phrase.include !== '0'}
                        onChange={(e) => {
                          const phrases = [...(resume.summary_phrases || [])];
                          phrases[i] = { ...phrases[i], include: e.target.checked ? '1' : '0' };
                          setResume({ ...resume, summary_phrases: phrases });
                        }}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 shrink-0 w-3 h-3"
                        title={phrase.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                      />
                      <div className="flex-1 min-w-0">
                        {editingSummaryPhraseIndex === i ? (
                          <>
                            <textarea
                              value={phrase.text}
                              onChange={(e) => updateSummaryPhraseText(i, e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); setEditingSummaryPhraseIndex(null); } }}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-1"
                              rows={2}
                              autoFocus
                            />
                            <input
                              key={(phrase.tags || []).join(',')}
                              type="text"
                              defaultValue={(phrase.tags || []).join(', ')}
                              onBlur={(e) => updateSummaryPhraseTags(i, e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateSummaryPhraseTags(i, e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean));
                                  setEditingSummaryPhraseIndex(null);
                                }
                              }}
                              className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                              placeholder="tags (comma-separated)"
                            />
                          </>
                        ) : (
                          <div onClick={() => setEditingSummaryPhraseIndex(i)} className="cursor-pointer">
                            <div className="text-sm text-gray-700 leading-snug">{phrase.text}</div>
                            {(phrase.tags || []).length > 0 && (
                              <div className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">{phrase.tags.join(', ')}</div>
                            )}
                          </div>
                        )}
                      </div>
                      <button onClick={() => { if (editingSummaryPhraseIndex === i) setEditingSummaryPhraseIndex(null); removeSummaryPhrase(i); }} className="text-gray-400 hover:text-red-500 shrink-0 mt-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input type="text" value={newSummaryPhrase} onChange={(e) => setNewSummaryPhrase(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSummaryPhrase()} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Add summary phrase..." autoComplete="off" />
                <button onClick={addSummaryPhrase} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Skills */}
            <div data-help-target="mainapp.resumes.resumeN.skills" className={sectionGenTypes.skills === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-bold text-gray-700">Skills</label>
                {renderGenTypeButton('skills')}
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {resume.skills.map((skill, i) => (
                  <div key={i} data-skill-index={i} className={`flex items-start gap-1 px-2 py-1.5 bg-gray-100 rounded-lg text-sm min-w-[140px] max-w-[220px]${skill.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={skill.include !== '0'}
                      onChange={(e) => {
                        const skills = [...resume.skills];
                        skills[i] = { ...skills[i], include: e.target.checked ? '1' : '0' };
                        setResume({ ...resume, skills });
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5 shrink-0 w-3 h-3"
                      title={skill.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    <div className="flex-1 min-w-0">
                      {editingSkillIndex === i ? (
                        <>
                          <input type="text" value={skill.title} onChange={(e) => updateSkill(i, e.target.value)} onKeyDown={(e) => e.key === 'Enter' && setEditingSkillIndex(null)} className="w-full px-1 py-0 border border-gray-300 rounded text-sm mb-1" autoFocus autoComplete="off" />
                          <input
                            key={(skill.tags || []).join(',')}
                            type="text"
                            defaultValue={(skill.tags || []).join(', ')}
                            onBlur={(e) => updateSkillTags(i, e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateSkillTags(i, e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean));
                                setEditingSkillIndex(null);
                              }
                            }}
                            className="w-full px-1 py-0 border border-gray-100 rounded text-xs text-gray-500"
                            placeholder="tags (comma-separated)"
                          />
                        </>
                      ) : (
                        <div onClick={() => setEditingSkillIndex(i)} className="cursor-pointer">
                          <div className="truncate leading-tight">{skill.title}</div>
                          {(skill.tags || []).length > 0 && (
                            <div className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">{skill.tags.join(', ')}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <button onClick={() => { if (editingSkillIndex === i) setEditingSkillIndex(null); removeSkill(i); }} className="text-gray-400 hover:text-red-500 shrink-0 mt-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {resume.skills.map((_, i) => (
                <div key={`skill-rec-${i}`}>{renderInlineRecs(`skills[${i}]`)}</div>
              ))}
              {/* Section-level and out-of-range skills recommendations */}
              {renderInlineRecs('skills')}
              {(() => {
                const indexedPaths = new Set(resume.skills.map((_, i) => `skills[${i}]`));
                indexedPaths.add('skills');
                const unmatchedPaths = [...new Set(
                  pendingRecs
                    .filter(r => (r.path === 'skills' || r.path.startsWith('skills[')) && !indexedPaths.has(r.path))
                    .map(r => r.path)
                )];
                return unmatchedPaths.map(p => <div key={`extra-skill-${p}`}>{renderInlineRecs(p)}</div>);
              })()}
              <div className="flex gap-2">
                <input type="text" value={newSkill} onChange={(e) => setNewSkill(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSkill()} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Add skill..." autoComplete="off" />
                <button onClick={addSkill} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>


            <hr className="border-gray-200" />

            {/* Experience */}
            <div data-help-target="mainapp.resumes.resumeN.experience" className={sectionGenTypes.experience === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-bold text-gray-700">Experience</label>
                {renderGenTypeButton('experience')}
              </div>
              <div className="space-y-4 mb-3">
                {getExperience().map((company, companyIdx) => (
                  <div key={companyIdx} className={`border border-gray-200 rounded-lg p-3${company.include === '0' ? ' opacity-40' : ''}`}>
                    {/* Company Header */}
                    <div className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={company.include !== '0'}
                        onChange={(e) => updateCompany(companyIdx, { include: e.target.checked ? '1' : '0' })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1.5 shrink-0"
                        title={company.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                      />
                      <Building2 className="w-5 h-5 text-gray-500 mt-1" />
                      <div className="flex-1">
                        {editingCompanyIndex === companyIdx ? (
                          <div className="space-y-2">
                            <input type="text" value={company.company} onChange={(e) => updateCompany(companyIdx, { company: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Company Name" />
                            <div className="grid grid-cols-3 gap-2">
                              <input type="text" value={(company.url || []).join(', ')} onChange={(e) => updateCompany(companyIdx, { url: e.target.value.split(',').map(u => u.trim()).filter(Boolean) })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Company URL(s), comma-separated" />
                              <input type="text" value={company.employees || ''} onChange={(e) => updateCompany(companyIdx, { employees: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="# Employees" />
                              <input type="text" value={company.location || ''} onChange={(e) => updateCompany(companyIdx, { location: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Location" />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <input type="text" value={company.startDate} onChange={(e) => updateCompany(companyIdx, { startDate: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Start Year / Month" />
                              <input type="text" value={company.endDate || ''} onChange={(e) => updateCompany(companyIdx, { endDate: e.target.value })} disabled={company.current} className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100" placeholder="End Year / Month" />
                              <label className="flex items-center text-sm">
                                <input type="checkbox" checked={company.current} onChange={(e) => updateCompany(companyIdx, { current: e.target.checked, endDate: e.target.checked ? undefined : company.endDate })} className="mr-2" />
                                Current
                              </label>
                            </div>
                            <textarea value={company.description || ''} onChange={(e) => updateCompany(companyIdx, { description: e.target.value })} className="px-2 py-1 border border-gray-300 rounded" rows={2} placeholder="Short company description" style={{ fontFamily: RESUME_FONT, fontSize: '10.5pt', lineHeight: 1.45, width: RESUME_WIDTH }} />
                            <button onClick={() => setEditingCompanyIndex(null)} className="text-xs text-primary-600 hover:underline">Done editing</button>
                          </div>
                        ) : (
                          <div>
                            <span className="font-medium">{company.company || 'New Company'}</span>
                            {company.location && <span className="text-gray-500 text-sm ml-2">({company.location})</span>}
                            <div className="text-xs text-gray-500">{company.startDate} - {company.current ? 'Present' : company.endDate}</div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditingCompanyIndex(editingCompanyIndex === companyIdx ? null : companyIdx)} className="text-gray-500 hover:text-gray-600">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => moveCompany(companyIdx, 'up')} disabled={companyIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button onClick={() => moveCompany(companyIdx, 'down')} disabled={companyIdx === getExperience().length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button onClick={() => removeCompany(companyIdx)} className="text-gray-500 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Roles */}
                    <div className="ml-6 space-y-3">
                      {company.roles.map((role, roleIdx) => (
                        <div key={roleIdx} className={`border-l-2 border-gray-200 pl-3${role.include === '0' ? ' opacity-50' : ''}`}>
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={role.include !== '0'}
                              onChange={(e) => updateRole(companyIdx, roleIdx, { include: e.target.checked ? '1' : '0' })}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 shrink-0 w-3.5 h-3.5"
                              title={role.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                            />
                            <Briefcase className="w-4 h-4 text-gray-500 mt-1" />
                            <div className="flex-1">
                              {editingRoleIndex?.companyIdx === companyIdx && editingRoleIndex?.roleIdx === roleIdx ? (
                                <div className="space-y-2">
                                  <input type="text" value={role.title} onChange={(e) => updateRole(companyIdx, roleIdx, { title: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Role Title" />
                                  <div className="grid grid-cols-3 gap-2">
                                    <input type="text" value={role.startDate} onChange={(e) => updateRole(companyIdx, roleIdx, { startDate: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Start Year / Month" />
                                    <input type="text" value={role.endDate || ''} onChange={(e) => updateRole(companyIdx, roleIdx, { endDate: e.target.value })} disabled={role.current} className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100" placeholder="End Year / Month" />
                                    <label className="flex items-center text-sm">
                                      <input type="checkbox" checked={role.current} onChange={(e) => updateRole(companyIdx, roleIdx, { current: e.target.checked, endDate: e.target.checked ? undefined : role.endDate })} className="mr-2" />
                                      Current
                                    </label>
                                  </div>
                                  <input type="text" value={role.location || ''} onChange={(e) => updateRole(companyIdx, roleIdx, { location: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Location (if different from company)" />
                                  <button onClick={() => setEditingRoleIndex(null)} className="text-xs text-primary-600 hover:underline">Done editing</button>
                                </div>
                              ) : (
                                <div>
                                  <span className="text-sm font-medium">{role.title || 'New Role'}</span>
                                  <div className="text-xs text-gray-500">{role.startDate} - {role.current ? 'Present' : role.endDate}</div>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => setEditingRoleIndex(editingRoleIndex?.companyIdx === companyIdx && editingRoleIndex?.roleIdx === roleIdx ? null : { companyIdx, roleIdx })} className="text-gray-500 hover:text-gray-600">
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button onClick={() => moveRole(companyIdx, roleIdx, 'up')} disabled={roleIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                                <ChevronUp className="w-3 h-3" />
                              </button>
                              <button onClick={() => moveRole(companyIdx, roleIdx, 'down')} disabled={roleIdx === company.roles.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                                <ChevronDown className="w-3 h-3" />
                              </button>
                              <button onClick={() => removeRole(companyIdx, roleIdx)} className="text-gray-500 hover:text-red-500">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>

                          {/* Bullets */}
                          <div className="ml-5 mt-2 space-y-1">
                            {role.bullets.map((bullet, bulletIdx) => (
                              <div key={bulletIdx} className={bullet.include === '0' ? 'opacity-50' : ''}>
                                <div className="flex items-start gap-2">
                                  <input
                                    type="checkbox"
                                    checked={bullet.include !== '0'}
                                    onChange={(e) => updateBullet(companyIdx, roleIdx, bulletIdx, { include: e.target.checked ? '1' : '0' })}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1.5 shrink-0 w-3 h-3"
                                    title={bullet.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                                  />
                                  <span className="text-gray-500 mt-1">•</span>
                                  <div className="flex-1">
                                    <BulletTextWithLineBreaks
                                      text={bullet.text}
                                      onChange={(text) => updateBullet(companyIdx, roleIdx, bulletIdx, { text })}
                                    />
                                    <input
                                      key={(bullet.tags || []).join(',')}
                                      type="text"
                                      defaultValue={(bullet.tags || []).join(', ')}
                                      onBlur={(e) => updateBullet(companyIdx, roleIdx, bulletIdx, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          updateBullet(companyIdx, roleIdx, bulletIdx, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                                          e.currentTarget.blur();
                                        }
                                      }}
                                      className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500 mt-1"
                                      placeholder="Tags (comma-separated)"
                                    />
                                  </div>
                                  <div className="flex gap-1">
                                    <button onClick={() => moveBullet(companyIdx, roleIdx, bulletIdx, 'up')} disabled={bulletIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                                      <ChevronUp className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => moveBullet(companyIdx, roleIdx, bulletIdx, 'down')} disabled={bulletIdx === role.bullets.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                                      <ChevronDown className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => removeBullet(companyIdx, roleIdx, bulletIdx)} className="text-gray-500 hover:text-red-500">
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                                {renderInlineRecs(`experience[${companyIdx}].roles[${roleIdx}].bullets[${bulletIdx}]`)}
                              </div>
                            ))}
                            <button onClick={() => addBullet(companyIdx, roleIdx)} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Add bullet
                            </button>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => addRole(companyIdx)} className="text-xs text-primary-600 hover:underline flex items-center gap-1 ml-1">
                        <Plus className="w-3 h-3" /> Add role
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={addCompany} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
                <Plus className="w-4 h-4" /> Add Company
              </button>
            </div>


            <hr className="border-gray-200" />

            {/* Education */}
            <div data-help-target="mainapp.resumes.resumeN.education" className={sectionGenTypes.education === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-bold text-gray-700">Education</label>
                {renderGenTypeButton('education')}
              </div>
              <div className="space-y-2 mb-3">
                {resume.education.map((edu, i) => (
                  <div key={i}>
                    <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${edu.include === '0' ? ' opacity-40' : ''}`}>
                      <input
                        type="checkbox"
                        checked={edu.include !== '0'}
                        onChange={(e) => updateEducation(i, { include: e.target.checked ? '1' : '0' })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                        title={edu.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                      />
                      {editingEducationIndex === i ? (
                        <div className="flex-1 space-y-1">
                          <div className="flex gap-2 items-center">
                            <input type="text" value={edu.title} onChange={(e) => updateEducation(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title/Degree" />
                            <input type="text" value={edu.org} onChange={(e) => updateEducation(i, { org: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Organization" />
                            <input type="text" value={edu.url || ''} onChange={(e) => updateEducation(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                            <input type="text" value={edu.year || ''} onChange={(e) => updateEducation(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                          </div>
                          <input
                            key={(edu.tags || []).join(',')}
                            type="text"
                            defaultValue={(edu.tags || []).join(', ')}
                            onBlur={(e) => updateEducation(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateEducation(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                                e.currentTarget.blur();
                              }
                            }}
                            className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                            placeholder="Tags (comma-separated)"
                          />
                        </div>
                      ) : (
                        <div className="flex-1">
                          <span className="text-sm font-medium">{edu.title}</span>
                          <span className="text-sm text-gray-500"> — {edu.org}</span>
                          {edu.year && <span className="text-xs text-gray-500 ml-2">({edu.year})</span>}
                        </div>
                      )}
                      <button onClick={() => setEditingEducationIndex(editingEducationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveEducation(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveEducation(i, 'down')} disabled={i === resume.education.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeEducation(i)} className="text-gray-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {renderInlineRecs(`education[${i}]`)}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Title/Degree</label>
                  <input type="text" value={newEduTitle} onChange={(e) => setNewEduTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="B.S. Computer Science" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Organization</label>
                  <input type="text" value={newEduOrg} onChange={(e) => setNewEduOrg(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="University Name" />
                </div>
                <div className="w-24">
                  <label className="block text-xs text-gray-500 mb-1">Year</label>
                  <input type="text" value={newEduYear} onChange={(e) => setNewEduYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addEducation()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2020" />
                </div>
                <button onClick={addEducation} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Awards */}
            <div data-help-target="mainapp.resumes.resumeN.awards" className={sectionGenTypes.awards === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-bold text-gray-700">Awards</label>
                {renderGenTypeButton('awards')}
              </div>
              <div className="space-y-2 mb-3">
                {resume.awards.map((award, i) => (
                  <div key={i}>
                    <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${award.include === '0' ? ' opacity-40' : ''}`}>
                      <input
                        type="checkbox"
                        checked={award.include !== '0'}
                        onChange={(e) => updateAward(i, { include: e.target.checked ? '1' : '0' })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                        title={award.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                      />
                      {editingAwardIndex === i ? (
                        <div className="flex-1 space-y-1">
                          <div className="flex gap-2 items-center">
                            <input type="text" value={award.title} onChange={(e) => updateAward(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                            <input type="text" value={award.issuer || ''} onChange={(e) => updateAward(i, { issuer: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Issuer" />
                            <input type="text" value={award.year || ''} onChange={(e) => updateAward(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                          </div>
                          <div className="flex gap-2 items-center">
                            <input type="text" value={award.reward || ''} onChange={(e) => updateAward(i, { reward: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Reward" />
                            <input type="text" value={award.url || ''} onChange={(e) => updateAward(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          </div>
                          <input
                            key={(award.tags || []).join(',')}
                            type="text"
                            defaultValue={(award.tags || []).join(', ')}
                            onBlur={(e) => updateAward(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateAward(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                                e.currentTarget.blur();
                              }
                            }}
                            className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                            placeholder="Tags (comma-separated)"
                          />
                        </div>
                      ) : (
                        <div className="flex-1">
                          <span className="text-sm font-medium">{award.title}</span>
                          {award.issuer && <span className="text-sm text-gray-500"> — {award.issuer}</span>}
                          {award.reward && <span className="text-sm text-gray-500"> — {award.reward}</span>}
                          {award.year && <span className="text-xs text-gray-500 ml-2">({award.year})</span>}
                        </div>
                      )}
                      <button onClick={() => setEditingAwardIndex(editingAwardIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveAward(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveAward(i, 'down')} disabled={i === resume.awards.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeAward(i)} className="text-gray-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {renderInlineRecs(`awards[${i}]`)}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Title</label>
                  <input type="text" value={newAwardTitle} onChange={(e) => setNewAwardTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Award Name" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Issuer</label>
                  <input type="text" value={newAwardIssuer} onChange={(e) => setNewAwardIssuer(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Awarding organization" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Reward</label>
                  <input type="text" value={newAwardReward} onChange={(e) => setNewAwardReward(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Reward provided" />
                </div>
                <div className="w-24">
                  <label className="block text-xs text-gray-500 mb-1">Year</label>
                  <input type="text" value={newAwardYear} onChange={(e) => setNewAwardYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addAward()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
                </div>
                <button onClick={addAward} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Presentations */}
            <div data-help-target="mainapp.resumes.resumeN.presentations" className={sectionGenTypes.presentations === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-bold text-gray-700">Presentations & Speaking</label>
                {renderGenTypeButton('presentations')}
              </div>
              <div className="space-y-2 mb-3">
                {(resume.presentations || []).map((presentation, i) => (
                  <div key={i}>
                    <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${presentation.include === '0' ? ' opacity-40' : ''}`}>
                      <input
                        type="checkbox"
                        checked={presentation.include !== '0'}
                        onChange={(e) => updatePresentation(i, { include: e.target.checked ? '1' : '0' })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                        title={presentation.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                      />
                      {editingPresentationIndex === i ? (
                        <div className="flex-1 space-y-1">
                          <div className="flex gap-2 items-center">
                            <input type="text" value={presentation.title} onChange={(e) => updatePresentation(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                            <input type="text" value={presentation.event || ''} onChange={(e) => updatePresentation(i, { event: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Event/Location" />
                            <input type="text" value={presentation.url || ''} onChange={(e) => updatePresentation(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                            <input type="text" value={presentation.year || ''} onChange={(e) => updatePresentation(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                          </div>
                          <input
                            key={(presentation.tags || []).join(',')}
                            type="text"
                            defaultValue={(presentation.tags || []).join(', ')}
                            onBlur={(e) => updatePresentation(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updatePresentation(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                                e.currentTarget.blur();
                              }
                            }}
                            className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                            placeholder="Tags (comma-separated)"
                          />
                        </div>
                      ) : (
                        <div className="flex-1">
                          <span className="text-sm font-medium">{presentation.title}</span>
                          {presentation.event && <span className="text-sm text-gray-500"> — {presentation.event}</span>}
                          {presentation.year && <span className="text-xs text-gray-500 ml-2">({presentation.year})</span>}
                        </div>
                      )}
                      <button onClick={() => setEditingPresentationIndex(editingPresentationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => movePresentation(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => movePresentation(i, 'down')} disabled={i === (resume.presentations || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removePresentation(i)} className="text-gray-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {renderInlineRecs(`presentations[${i}]`)}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Title</label>
                  <input type="text" value={newPresentationTitle} onChange={(e) => setNewPresentationTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Presentation or Keynote Title" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Event/Location</label>
                  <input type="text" value={newPresentationEvent} onChange={(e) => setNewPresentationEvent(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Event name or location" />
                </div>
                <div className="w-24">
                  <label className="block text-xs text-gray-500 mb-1">Year</label>
                  <input type="text" value={newPresentationYear} onChange={(e) => setNewPresentationYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPresentation()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
                </div>
                <button onClick={addPresentation} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Publications */}
            <div data-help-target="mainapp.resumes.resumeN.publications" className={sectionGenTypes.publications === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-bold text-gray-700">Publications</label>
                {renderGenTypeButton('publications')}
              </div>
              <div className="space-y-2 mb-3">
                {(resume.publications || []).map((pub, i) => (
                  <div key={i}>
                    <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${pub.include === '0' ? ' opacity-40' : ''}`}>
                      <input
                        type="checkbox"
                        checked={pub.include !== '0'}
                        onChange={(e) => updatePublication(i, { include: e.target.checked ? '1' : '0' })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                        title={pub.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                      />
                      {editingPublicationIndex === i ? (
                        <div className="flex-1 space-y-1">
                          <div className="flex gap-2 items-center">
                            <input type="text" value={pub.title} onChange={(e) => updatePublication(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                            <input type="text" value={pub.url || ''} onChange={(e) => updatePublication(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                            <input type="text" value={pub.year || ''} onChange={(e) => updatePublication(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                          </div>
                          <input
                            key={(pub.tags || []).join(',')}
                            type="text"
                            defaultValue={(pub.tags || []).join(', ')}
                            onBlur={(e) => updatePublication(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updatePublication(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                                e.currentTarget.blur();
                              }
                            }}
                            className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                            placeholder="Tags (comma-separated)"
                          />
                        </div>
                      ) : (
                        <div className="flex-1">
                          {pub.url ? (
                            <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:underline">{pub.title}</a>
                          ) : (
                            <span className="text-sm font-medium">{pub.title}</span>
                          )}
                          {pub.year && <span className="text-xs text-gray-500 ml-2">({pub.year})</span>}
                        </div>
                      )}
                      <button onClick={() => setEditingPublicationIndex(editingPublicationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => movePublication(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => movePublication(i, 'down')} disabled={i === (resume.publications || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removePublication(i)} className="text-gray-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {renderInlineRecs(`publications[${i}]`)}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Title</label>
                  <input type="text" value={newPubTitle} onChange={(e) => setNewPubTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Publication Title" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">URL (optional)</label>
                  <input type="text" value={newPubUrl} onChange={(e) => setNewPubUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://example.com/article" />
                </div>
                <div className="w-24">
                  <label className="block text-xs text-gray-500 mb-1">Year</label>
                  <input type="text" value={newPubYear} onChange={(e) => setNewPubYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPublication()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
                </div>
                <button onClick={addPublication} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Portfolios */}
            <div data-help-target="mainapp.resumes.resumeN.portfolios" className={sectionGenTypes.portfolios === 'omit' ? 'opacity-50' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-bold text-gray-700">Portfolios</label>
                {renderGenTypeButton('portfolios')}
              </div>
              <div className="space-y-2 mb-3">
                {(resume.portfolios || []).map((port, i) => (
                  <div key={i}>
                    <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${port.include === '0' ? ' opacity-40' : ''}`}>
                      <input
                        type="checkbox"
                        checked={port.include !== '0'}
                        onChange={(e) => updatePortfolio(i, { include: e.target.checked ? '1' : '0' })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                        title={port.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                      />
                      {editingPortfolioIndex === i ? (
                        <div className="flex-1 space-y-1">
                          <div className="flex gap-2 items-center">
                            <input type="text" value={port.title} onChange={(e) => updatePortfolio(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                            <input type="text" value={port.url || ''} onChange={(e) => updatePortfolio(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                            <input type="text" value={port.year || ''} onChange={(e) => updatePortfolio(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                          </div>
                          <input
                            key={(port.tags || []).join(',')}
                            type="text"
                            defaultValue={(port.tags || []).join(', ')}
                            onBlur={(e) => updatePortfolio(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updatePortfolio(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                                e.currentTarget.blur();
                              }
                            }}
                            className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                            placeholder="Tags (comma-separated)"
                          />
                        </div>
                      ) : (
                        <div className="flex-1">
                          {port.url ? (
                            <a href={port.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:underline">{port.title}</a>
                          ) : (
                            <span className="text-sm font-medium">{port.title}</span>
                          )}
                          {port.year && <span className="text-xs text-gray-500 ml-2">({port.year})</span>}
                        </div>
                      )}
                      <button onClick={() => setEditingPortfolioIndex(editingPortfolioIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => movePortfolio(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => movePortfolio(i, 'down')} disabled={i === (resume.portfolios || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removePortfolio(i)} className="text-gray-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {renderInlineRecs(`portfolios[${i}]`)}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Title</label>
                  <input type="text" value={newPortTitle} onChange={(e) => setNewPortTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Portfolio Title" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">URL (optional)</label>
                  <input type="text" value={newPortUrl} onChange={(e) => setNewPortUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://example.com/portfolio" />
                </div>
                <div className="w-24">
                  <label className="block text-xs text-gray-500 mb-1">Year</label>
                  <input type="text" value={newPortYear} onChange={(e) => setNewPortYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPortfolio()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
                </div>
                <button onClick={addPortfolio} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

          </div>
        )}

        <div className="relative flex items-center justify-between p-4 border-t">
          {/* Delete button - only shown when editing an existing resume */}
          <div>
            {resumename && onDeleteResume && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving || importing || deleting}
                className="flex items-center px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Resume
              </button>
            )}
          </div>

          {/* Centered View dropdown — HTML / PDF — only for saved resumes */}
          {resumename && (
            <div ref={viewMenuRef} className="absolute left-1/2 -translate-x-1/2">
              <button
                type="button"
                onClick={() => setViewMenuOpen(v => !v)}
                disabled={saving || importing || deleting || exporting !== null}
                className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-100 disabled:opacity-50 text-sm"
                title="View this resume as HTML or PDF"
                aria-haspopup="menu"
                aria-expanded={viewMenuOpen}
              >
                {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                <span>View</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {viewMenuOpen && (
                <div
                  role="menu"
                  className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-10 min-w-[140px] bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setViewMenuOpen(false);
                      if (user?.username) {
                        const url = `https://${user.username.toLowerCase()}.skillsnap.me/${masterResumePath}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    disabled={!user?.username}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 text-left"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>HTML</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setViewMenuOpen(false);
                      handleExportFull('pdf');
                    }}
                    disabled={exporting !== null}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 text-left"
                  >
                    {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    <span>PDF</span>
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex space-x-3">
            <button onClick={handleClose} disabled={importing} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50">
              Cancel
            </button>
            <button
              onClick={() => handleSave(false)}
              disabled={saving || loading || importing}
              title="Save without closing — useful when you want tag regeneration to run and then keep editing"
              className="px-4 py-2 bg-primary-50 text-primary-700 border border-primary-300 rounded-lg hover:bg-primary-100 disabled:opacity-50 flex items-center"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving || loading || importing}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save and Close
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                Delete Resume
              </h3>
              <p className="text-sm text-gray-600 text-center mb-1">
                Are you sure you want to delete <span className="font-medium">"{resumename}"</span>?
              </p>
              <p className="text-sm text-red-600 text-center font-medium">
                This action cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteResume}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <UpgradePromptModal
        isOpen={showAiUpgradeModal}
        onClose={() => setShowAiUpgradeModal(false)}
        limitType="ai"
        onNavigateToSettings={() => {
          setShowAiUpgradeModal(false);
          onClose();
        }}
      />
    </div>
  );
}
